/**
 * KMA Store Locator JS - Shopify Asset
 * Scoped, self-contained Leaflet Map & Custom Search Select dropdowns
 */

(function () {
  'use strict';

  // Dynamic loader for Leaflet CSS & JS CDN dependencies
  function loadLeaflet(callback) {
    if (window.L) {
      callback();
      return;
    }

    // Load Leaflet CSS
    if (!document.querySelector('link[href*="leaflet.css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    // Load Leaflet JS
    if (!document.querySelector('script[src*="leaflet.js"]')) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = callback;
      document.head.appendChild(script);
    } else {
      // Check periodically in case script tag is injected but not loaded yet
      const interval = setInterval(() => {
        if (window.L) {
          clearInterval(interval);
          callback();
        }
      }, 100);
    }
  }

  // Province coordinates middle points fallback list
  const PROVINCE_CENTERS = {
    'กรุงเทพมหานคร': [13.7563, 100.5018],
    'ชลบุรี': [13.3611, 100.9847],
    'สมุทรปราการ': [13.5991, 100.5968],
    'นนทบุรี': [13.8591, 100.4988],
    'ปทุมธานี': [14.0208, 100.5250],
    'ขอนแก่น': [16.4322, 102.8236],
    'สุราษฎร์ธานี': [9.1399, 99.3323],
    'ภูเก็ต': [7.8804, 98.3922],
    'นครศรีธรรมราช': [8.4333, 99.9667],
    'สงขลา': [7.1898, 100.5954],
    'ระยอง': [12.6815, 101.2813],
    'อุดรธานี': [17.4138, 102.7872],
    'เชียงใหม่': [18.7883, 98.9853]
  };

  function initStoreLocator(sectionContainer) {
    if (!sectionContainer) return;
    
    // Check if already initialized
    if (sectionContainer.dataset.initialized === 'true') return;
    sectionContainer.dataset.initialized = 'true';

    const sectionId = sectionContainer.getAttribute('data-section-id');
    const locale = sectionContainer.getAttribute('data-locale') || 'th';
    
    // Config labels based on locale
    const labels = {
      allProvinces: locale === 'th' ? 'จังหวัดทั้งหมด' : 'All Provinces',
      allTypes: locale === 'th' ? 'ประเภทร้านทั้งหมด' : 'All Store Types',
      callBtn: locale === 'th' ? 'โทรสอบถาม' : 'Call Store',
      directionBtn: locale === 'th' ? 'ดูเส้นทาง' : 'Directions'
    };

    // Load config from window settings
    const config = (window.KmaStoreSettings && window.KmaStoreSettings[sectionId]) || {
      itemsPerPage: 12,
      enablePhone: true,
      enableHours: true,
      enableFacebook: true,
      enableLoadMore: true,
      translations: labels
    };

    // Load store database
    const jsonTag = document.getElementById('kma-stores-json');
    if (!jsonTag) {
      console.error('KMA Store Locator: JSON data tag not found.');
      return;
    }

    let KMA_STORES = [];
    try {
      KMA_STORES = JSON.parse(jsonTag.textContent);
      // Sort stores by sort_order
      KMA_STORES.sort((a, b) => {
        const orderA = parseInt(a.sort_order) || 9999;
        const orderB = parseInt(b.sort_order) || 9999;
        return orderA - orderB;
      });
    } catch (e) {
      console.error('KMA Store Locator: Failed to parse store JSON.', e);
      return;
    }

    // Custom Select elements wired inside this section container
    const searchInput = sectionContainer.querySelector('#kma-store-search-input');
    const clearSearchBtn = sectionContainer.querySelector('#kma-store-clear-search');
    const resetFiltersBtn = sectionContainer.querySelector('#kma-store-reset-filters');
    const resultsCount = sectionContainer.querySelector('#kma-store-count-val');
    const storeGrid = sectionContainer.querySelector('#kma-store-grid-list');
    const loadMoreBtn = sectionContainer.querySelector('#kma-store-load-more-btn');
    const loadMoreWrapper = sectionContainer.querySelector('#kma-store-load-more-container');
    const emptyState = sectionContainer.querySelector('#kma-store-empty');
    const emptyResetBtn = sectionContainer.querySelector('#kma-store-empty-reset');
    const mapContainer = sectionContainer.querySelector('#kma-store-map-container');

    // Custom Dropdown Select DOM Elements
    const csProvinceEl   = sectionContainer.querySelector('#cs-province');
    const csTypeEl       = sectionContainer.querySelector('#cs-type');
    const csProvinceVal  = sectionContainer.querySelector('#cs-province-val');
    const csTypeVal      = sectionContainer.querySelector('#cs-type-val');
    const csProvinceList = sectionContainer.querySelector('#cs-province-list');
    const csTypeList     = sectionContainer.querySelector('#cs-type-list');
    const csProvinceSearch = sectionContainer.querySelector('#cs-province-search');

    // Filtering State
    let searchQuery = '';
    let selectedProvinces = [];
    let selectedTypes = [];
    let visibleCount = config.itemsPerPage;

    let map;
    let mapMarkers = [];

    // Touch device detection helper to prevent virtual keyboard popup bugs
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Helper: extract coordinates from store details
    function getStoreCoordinates(store) {
      if (store.latitude && store.longitude && store.latitude !== "" && store.longitude !== "") {
        const lat = parseFloat(store.latitude);
        const lng = parseFloat(store.longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          return [lat, lng];
        }
      }
      
      const base = PROVINCE_CENTERS[store.province] || PROVINCE_CENTERS['กรุงเทพมหานคร'] || [13.7563, 100.5018];
      
      // Hash the store code to yield stable offsets so markers don't stack directly
      let hash = 0;
      const key = store.store_code || store.name || "kma";
      for (let i = 0; i < key.length; i++) {
        hash = key.charCodeAt(i) + ((hash << 5) - hash);
      }
      
      const latOffset = ((hash % 50) / 1000) * 0.15;
      const lngOffset = ((((hash >> 8) % 50) / 1000) * 0.15);
      
      return [base[0] + latOffset, base[1] + lngOffset];
    }

    // Toggle Custom Dropdown Open/Close
    function toggleCustomSelect(key) {
      const elMap = { province: csProvinceEl, type: csTypeEl };
      const el = elMap[key];
      if (!el) return;
      const wasOpen = el.classList.contains('open');
      closeAllCustomSelects();
      if (!wasOpen) {
        el.classList.add('open');
        // Focus search input on desktop
        if (key === 'province' && csProvinceSearch && !isTouchDevice) {
          setTimeout(() => csProvinceSearch.focus(), 60);
        }
      }
    }

    function closeAllCustomSelects() {
      [csProvinceEl, csTypeEl].forEach(el => {
        if (el && el.classList.contains('open')) {
          el.classList.remove('open');
        }
      });
    }

    /**
     * bindOptionTap — Mobile touchscreen tap helper.
     * Binds touchstart/touchend with a touch-delta check so dragging/scrolling
     * the dropdown list does NOT trigger selections, but normal taps respond instantly.
     */
    function bindOptionTap(el, fn) {
      let startX, startY;
      el.addEventListener('touchstart', e => {
        // Ignore taps on interactive inner elements like call/direction buttons
        if (e.target.closest('a, button, .kma-store-btn-outline, .kma-store-btn-primary')) return;
        
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });
      
      el.addEventListener('touchend', e => {
        // Ignore taps on interactive inner elements like call/direction buttons
        if (e.target.closest('a, button, .kma-store-btn-outline, .kma-store-btn-primary')) return;
        
        const moveX = Math.abs(e.changedTouches[0].clientX - startX);
        const moveY = Math.abs(e.changedTouches[0].clientY - startY);
        // Tap threshold: 6px
        if (moveX < 6 && moveY < 6) {
          e.preventDefault(); // prevent ghost click
          fn();
        }
      });
      
      el.addEventListener('click', fn);
    }

    // Build lists items inside dropdown lists
    function buildCustomOptions(listEl, options, selectedValue, onSelect) {
      if (!listEl) return;
      listEl.innerHTML = '';
      options.forEach(({ value, label }) => {
        const li = document.createElement('li');
        li.className = 'kma-select__option' + (value === selectedValue ? ' selected' : '');
        li.dataset.value = value;
        li.innerHTML = `
          <svg class="kma-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          <span>${label}</span>`;
        bindOptionTap(li, () => {
          onSelect(value, label);
          closeAllCustomSelects();
        });
        listEl.appendChild(li);
      });
    }

    function setCustomSelectValue(valEl, label) {
      if (valEl) valEl.textContent = label;
    }

    function refreshOptionHighlight(listEl, selectedValue) {
      if (!listEl) return;
      listEl.querySelectorAll('.kma-select__option').forEach(li => {
        li.classList.toggle('selected', li.dataset.value === selectedValue);
      });
    }

    // 1. Build Custom Dropdowns
    let _allProvinces = [];
    let _allTypes = [];

    function setupFilters() {
      const provinces = new Set();
      const types = new Set();

      KMA_STORES.forEach(store => {
        if (store.province) {
          provinces.add(store.province);
        }
        if (store.store_group) {
          types.add(JSON.stringify({ group: store.store_group, label: store.store_type }));
        }
      });

      _allProvinces = Array.from(provinces).sort((a, b) => a.localeCompare(b, 'th'));
      _allTypes     = Array.from(types).map(t => JSON.parse(t)).sort((a, b) => a.group.localeCompare(b.group));

      _buildProvinceOptions();
      _buildTypeOptions();
    }

    function _buildProvinceOptions() {
      if (!csProvinceList) return;
      csProvinceList.innerHTML = '';

      // Update trigger value label
      if (selectedProvinces.length === 0) {
        setCustomSelectValue(csProvinceVal, labels.allProvinces);
      } else {
        setCustomSelectValue(csProvinceVal, selectedProvinces.join(', '));
      }

      // Add "All" option
      const liAll = document.createElement('li');
      liAll.className = 'kma-select__option' + (selectedProvinces.length === 0 ? ' selected' : '');
      liAll.dataset.value = '';
      liAll.innerHTML = `
        <svg class="kma-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
        <span>${labels.allProvinces}</span>`;
      
      bindOptionTap(liAll, () => {
        selectedProvinces = [];
        setCustomSelectValue(csProvinceVal, labels.allProvinces);
        _buildProvinceOptions();
        _buildTypeOptions();
        checkResetBtn();
        visibleCount = config.itemsPerPage;
        renderStoreList();
        closeAllCustomSelects();
      });
      csProvinceList.appendChild(liAll);

      // Add multi-select province options
      _allProvinces.forEach(p => {
        const isSelected = selectedProvinces.includes(p);
        const li = document.createElement('li');
        li.className = 'kma-select__option' + (isSelected ? ' selected' : '');
        li.dataset.value = p;
        li.innerHTML = `
          <svg class="kma-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          <span>${p}</span>`;
        
        bindOptionTap(li, () => {
          const isSelected = selectedProvinces.includes(p);
          if (isSelected) {
            selectedProvinces = selectedProvinces.filter(val => val !== p);
            li.classList.remove('selected');
          } else {
            selectedProvinces.push(p);
            li.classList.add('selected');
          }
          
          if (selectedProvinces.length === 0) {
            setCustomSelectValue(csProvinceVal, labels.allProvinces);
          } else {
            setCustomSelectValue(csProvinceVal, selectedProvinces.join(', '));
          }
          
          // Toggle "All" option checkmark in-place
          const liAll = csProvinceList.querySelector('[data-value=""]');
          if (liAll) {
            liAll.classList.toggle('selected', selectedProvinces.length === 0);
          }
          
          _buildTypeOptions(); // Rebuild type list since it depends on active provinces
          checkResetBtn();
          visibleCount = config.itemsPerPage;
          renderStoreList();
        });
        
        csProvinceList.appendChild(li);
      });
    }

    function _buildTypeOptions() {
      if (!csTypeList) return;
      csTypeList.innerHTML = '';

      // Filter type options based on selected provinces
      const typesSet = new Set();
      KMA_STORES.forEach(store => {
        if (selectedProvinces.length > 0 && !selectedProvinces.includes(store.province)) return;
        if (store.store_group) {
          typesSet.add(JSON.stringify({
            group: store.store_group,
            label: store.store_type
          }));
        }
      });

      const activeTypes = Array.from(typesSet)
        .map(tp => JSON.parse(tp))
        .sort((a, b) => a.group.localeCompare(b.group));

      // Prune selectedTypes if they are no longer in the active list
      selectedTypes = selectedTypes.filter(g => activeTypes.some(at => at.group === g));

      // Update displayed trigger text
      if (selectedTypes.length === 0) {
        setCustomSelectValue(csTypeVal, labels.allTypes);
      } else {
        setCustomSelectValue(csTypeVal, selectedTypes.join(', '));
      }

      // Add "All" option
      const liAll = document.createElement('li');
      liAll.className = 'kma-select__option' + (selectedTypes.length === 0 ? ' selected' : '');
      liAll.dataset.value = '';
      liAll.innerHTML = `
        <svg class="kma-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
        <span>${labels.allTypes}</span>`;
      
      bindOptionTap(liAll, () => {
        selectedTypes = [];
        setCustomSelectValue(csTypeVal, labels.allTypes);
        _buildTypeOptions();
        checkResetBtn();
        visibleCount = config.itemsPerPage;
        renderStoreList();
        closeAllCustomSelects();
      });
      csTypeList.appendChild(liAll);

      // Add multi-select brand options
      activeTypes.forEach(tp => {
        const isSelected = selectedTypes.includes(tp.group);
        const li = document.createElement('li');
        li.className = 'kma-select__option' + (isSelected ? ' selected' : '');
        li.dataset.value = tp.group;
        
        const label = `${tp.group} (${tp.label || 'Retailer'})`;
        
        li.innerHTML = `
          <svg class="kma-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          <span>${label}</span>`;
        
        bindOptionTap(li, () => {
          const isSelected = selectedTypes.includes(tp.group);
          if (isSelected) {
            selectedTypes = selectedTypes.filter(g => g !== tp.group);
            li.classList.remove('selected');
          } else {
            selectedTypes.push(tp.group);
            li.classList.add('selected');
          }
          
          if (selectedTypes.length === 0) {
            setCustomSelectValue(csTypeVal, labels.allTypes);
          } else {
            setCustomSelectValue(csTypeVal, selectedTypes.join(', '));
          }
          
          // Toggle "All" option checkmark in-place
          const liAll = csTypeList.querySelector('[data-value=""]');
          if (liAll) {
            liAll.classList.toggle('selected', selectedTypes.length === 0);
          }
          
          checkResetBtn();
          visibleCount = config.itemsPerPage;
          renderStoreList();
        });
        
        csTypeList.appendChild(li);
      });
    }

    // 2. Render Cards list
    function renderStoreList() {
      if (!storeGrid) return;
      
      const filtered = KMA_STORES.filter(store => {
        // Text Search
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          const matchName = store.name && store.name.toLowerCase().includes(q);
          const matchAddress = store.address && store.address.toLowerCase().includes(q);
          const matchPostal = store.postal_code && store.postal_code.includes(q);
          const matchGroup = store.store_group && store.store_group.toLowerCase().includes(q);
          const matchType = store.store_type && store.store_type.toLowerCase().includes(q);
          const matchProvince = store.province && store.province.toLowerCase().includes(q);
          
          if (!matchName && !matchAddress && !matchPostal && !matchGroup && !matchType && !matchProvince) {
            return false;
          }
        }
        
        // Province (Multi-select)
        if (selectedProvinces.length > 0 && !selectedProvinces.includes(store.province)) {
          return false;
        }
        
        // Store Type (Multi-select)
        if (selectedTypes.length > 0 && !selectedTypes.includes(store.store_group)) {
          return false;
        }
        
        return true;
      });

      // Update count
      if (resultsCount) {
        resultsCount.textContent = filtered.length;
      }

      // Check empty state
      if (filtered.length === 0) {
        storeGrid.style.display = 'none';
        if (loadMoreWrapper) loadMoreWrapper.style.display = 'none';
        if (emptyState) emptyState.style.display = 'block';
        updateMapMarkers(KMA_STORES, []);
        return;
      }

      if (emptyState) emptyState.style.display = 'none';
      storeGrid.style.display = 'grid';

      // Paginated items
      const paginated = filtered.slice(0, visibleCount);
      storeGrid.innerHTML = '';
      
      paginated.forEach(store => {
        const card = createCardElement(store);
        storeGrid.appendChild(card);
      });

      // Toggle Load More button
      if (loadMoreWrapper && config.enableLoadMore) {
        if (filtered.length > visibleCount) {
          loadMoreWrapper.style.display = 'flex';
        } else {
          loadMoreWrapper.style.display = 'none';
        }
      }

      // Update markers on Leaflet map
      updateMapMarkers(KMA_STORES, filtered);
    }

    // 3. Generate Single Card element
    function createCardElement(store) {
      const card = document.createElement('article');
      card.className = 'kma-store-card';
      card.tabIndex = 0;
      card.dataset.storeCode = store.store_code;

      const name = store.name;
      const address = store.address;
      const type = store.store_type;
      const tel = store.telephone;

      // Fail-safe Google Maps link generation
      let mapUrl = store.map_url;
      if (!mapUrl || mapUrl.trim() === "" || mapUrl === "#") {
        if (store.latitude && store.longitude) {
          mapUrl = `https://www.google.com/maps/search/?api=1&query=${store.latitude},${store.longitude}`;
        } else {
          mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name + ' ' + address)}`;
        }
      }

      card.innerHTML = `
        <div class="kma-store-card-header">
          <span class="kma-store-badge">${store.store_group}</span>
          ${store.floor ? `<span class="kma-store-badge-floor">${store.floor}</span>` : ''}
          <h3 class="kma-store-card-title">${name}</h3>
          <span class="kma-store-card-type">${type}</span>
        </div>
        <div class="kma-store-card-body">
          <p class="kma-store-card-address" title="${address}">${address}</p>
          ${tel && config.enablePhone ? `
            <div class="kma-store-card-phone">
              <svg class="kma-store-phone-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
              <span>${tel}</span>
            </div>
          ` : ''}
        </div>
        <div class="kma-store-card-footer">
          ${tel && config.enablePhone ? `
            <a href="tel:${tel}" class="kma-store-btn-outline" onclick="event.stopPropagation();">
              ${config.translations.callBtn}
            </a>
          ` : `<span class="kma-store-btn-outline" style="opacity:0.3;cursor:not-allowed;" onclick="event.stopPropagation();">${config.translations.callBtn}</span>`}
          <a href="${mapUrl}" target="_blank" rel="noopener noreferrer" class="kma-store-btn-primary" onclick="event.stopPropagation();">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:14px;height:14px;">
              <polygon points="3 11 22 2 13 21 11 13 3 11"></polygon>
            </svg>
            <span>${config.translations.directionBtn}</span>
          </a>
        </div>
      `;

      // Card selection interaction with toggle support — use bindOptionTap to fix mobile touchscreen scroll-cancel bug
      bindOptionTap(card, () => {
        const isAlreadySelected = card.classList.contains('selected');
        if (isAlreadySelected) {
          card.classList.remove('selected');
          if (map) {
            map.closePopup();
            fitMapToActiveMarkers();
          }
        } else {
          highlightStoreCard(store.store_code, false);
          focusStoreOnMap(store.store_code);
        }
      });

      return card;
    }

    // 4. Initialize Leaflet Map with interactive layer switcher
    function initMap() {
      if (!mapContainer || !window.L) return;

      const bkk = PROVINCE_CENTERS['กรุงเทพมหานคร'] || [13.7563, 100.5018];
      
      map = window.L.map(mapContainer, {
        zoomControl: false
      }).setView(bkk, 11);

      window.L.control.zoom({ position: 'bottomright' }).addTo(map);

      // Define map styles
      const googleRoadmap = window.L.tileLayer('https://mt1.google.com/vt/lyrs=m&hl=' + locale + '&x={x}&y={y}&z={z}', {
        maxZoom: 19,
        attribution: '&copy; Google Maps'
      });

      const googleHybrid = window.L.tileLayer('https://mt1.google.com/vt/lyrs=y&hl=' + locale + '&x={x}&y={y}&z={z}', {
        maxZoom: 19,
        attribution: '&copy; Google Maps'
      });

      const cartoVoyager = window.L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        maxZoom: 19,
        attribution: '&copy; CartoDB'
      });

      const cartoLight = window.L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        maxZoom: 19,
        attribution: '&copy; CartoDB'
      });

      const cartoDark = window.L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        maxZoom: 19,
        attribution: '&copy; CartoDB'
      });

      // Default active style (Carto Voyager)
      cartoVoyager.addTo(map);

      // Map layers config mapping
      const baseMaps = {
        "Carto Voyager (พาสเทล)": cartoVoyager,
        "Google Maps (ปกติ)": googleRoadmap,
        "Google Hybrid (ดาวเทียม)": googleHybrid,
        "Carto Light (มินิมอลเทา)": cartoLight,
        "Carto Dark (โหมดสีดำ)": cartoDark
      };

      // Add layer control box (collapsed to a clean icon by default)
      window.L.control.layers(baseMaps, null, {
        position: 'topright',
        collapsed: true
      }).addTo(map);
    }

    // 5. Draw and Update Pins on Leaflet Map
    function updateMapMarkers(allStores, filteredStores) {
      if (!map || !window.L) return;

      // Clear previous markers
      mapMarkers.forEach(m => map.removeLayer(m));
      mapMarkers = [];

      if (allStores.length === 0) return;

      const bounds = [];
      const isFilteredMode = filteredStores && filteredStores.length > 0 && filteredStores.length < allStores.length;

      allStores.forEach(store => {
        const coords = getStoreCoordinates(store);
        const isActive = !isFilteredMode || filteredStores.some(f => f.store_code === store.store_code);

        if (isActive) {
          bounds.push(coords);
        }

        // Color and size settings for custom teardrop pins
        let pinColor = '#c98b6f'; // brand primary
        if (store.store_code && (store.store_code.includes('Online') || store.province === 'ออนไลน์')) {
          pinColor = '#3b82f6'; // online blue
        }

        let innerHTML = '';
        let size = [30, 38];
        let anchor = [15, 38];
        let popupOffset = [0, -32];

        if (isActive) {
          innerHTML = `
            <svg viewBox="0 0 24 30" width="30" height="38" style="filter: drop-shadow(0 2px 5px rgba(0,0,0,0.22)); display: block; cursor: pointer; transition: transform 0.15s ease;" onmouseover="this.style.transform='scale(1.15)'" onmouseout="this.style.transform='scale(1)'">
              <path d="M12 0C5.37 0 0 5.37 0 12c0 9 12 18 12 18s12-9 12-18c0-6.63-5.37-12-12-12z" fill="${pinColor}" stroke="#ffffff" stroke-width="1.8"/>
              <circle cx="12" cy="11" r="4.2" fill="#ffffff"/>
            </svg>
          `;
          size = [30, 38];
          anchor = [15, 38];
          popupOffset = [0, -32];
        } else {
          // Smaller, semi-transparent grey teardrop for inactive stores
          innerHTML = `
            <svg viewBox="0 0 24 30" width="22" height="28" style="filter: drop-shadow(0 1px 3px rgba(0,0,0,0.15)); display: block; opacity: 0.55; cursor: pointer; transition: transform 0.15s ease;" onmouseover="this.style.transform='scale(1.15)'" onmouseout="this.style.transform='scale(1)'">
              <path d="M12 0C5.37 0 0 5.37 0 12c0 9 12 18 12 18s12-9 12-18c0-6.63-5.37-12-12-12z" fill="#d1d1d6" stroke="#ffffff" stroke-width="1.5"/>
              <circle cx="12" cy="11" r="3.5" fill="#ffffff"/>
            </svg>
          `;
          size = [22, 28];
          anchor = [11, 28];
          popupOffset = [0, -24];
        }

        const customIcon = window.L.divIcon({
          className: 'kma-leaflet-pin',
          html: innerHTML,
          iconSize: size,
          iconAnchor: anchor
        });

        const marker = window.L.marker(coords, { icon: customIcon }).addTo(map);

        if (isActive) {
          marker.setZIndexOffset(1000);
        } else {
          marker.setZIndexOffset(0);
        }

        const name = store.name;
        const address = store.address;
        const mapBtnText = locale === 'th' ? 'ดูเส้นทาง' : 'Directions';

        // Fail-safe Google Maps link generation
        let mapUrl = store.map_url;
        if (!mapUrl || mapUrl.trim() === "" || mapUrl === "#") {
          if (store.latitude && store.longitude) {
            mapUrl = `https://www.google.com/maps/search/?api=1&query=${store.latitude},${store.longitude}`;
          } else {
            mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name + ' ' + address)}`;
          }
        }

        // Popup details bubble styled as a premium card
        const popupContent = `
          <div style="font-family: 'Plus Jakarta Sans', 'Noto Sans Thai', sans-serif; font-size: 13.5px; color: #2f2926; max-width: 240px; line-height: 1.5; padding: 14px; display: flex; flex-direction: column; gap: 5px;">
            <strong style="color: #2f2926; font-size: 15px; display: block; font-weight: 700; margin: 0;">${name}</strong>
            <div style="color: #746b66; font-size: 12.5px; line-height: 1.45; margin: 0; max-height: 85px; overflow-y: auto;">${address}</div>
            ${store.telephone ? `<div style="font-weight: 700; color: #c98b6f; font-size: 13px; margin: 2px 0 4px 0;">${store.telephone}</div>` : ''}
            <a href="${mapUrl}" target="_blank" rel="noopener noreferrer" style="display: block; width: 100%; text-align: center; background-color: #c98b6f; color: #ffffff; padding: 8px 12px; border-radius: 6px; font-weight: 700; text-decoration: none; font-size: 13px; transition: background-color 0.2s ease; margin-top: 4px;" onmouseover="this.style.backgroundColor='#b7795f'" onmouseout="this.style.backgroundColor='#c98b6f'">
              ${mapBtnText}
            </a>
          </div>
        `;

        marker.bindPopup(popupContent, {
          closeButton: false,
          offset: popupOffset
        });

        marker.on('click', () => {
          map.setView(coords, 14);
          highlightStoreCard(store.store_code, true);
        });

        marker.storeCode = store.store_code;
        mapMarkers.push(marker);
      });

      // Fit boundary frames
      if (bounds.length > 0) {
        if (bounds.length === 1) {
          map.setView(bounds[0], 14);
        } else {
          map.fitBounds(bounds, {
            padding: [40, 40],
            maxZoom: 13
          });
        }
      }
    }

    // Helper to auto-fit map bounds to currently active markers
    function fitMapToActiveMarkers() {
      if (!map) return;
      const bounds = [];
      mapMarkers.forEach(m => {
        if (m.options.zIndexOffset === 1000) {
          bounds.push(m.getLatLng());
        }
      });
      if (bounds.length > 0) {
        if (bounds.length === 1) {
          map.setView(bounds[0], 14);
        } else {
          map.fitBounds(bounds, {
            padding: [40, 40],
            maxZoom: 13
          });
        }
      }
    }

    // 6. Focus on a specific marker
    function focusStoreOnMap(storeCode) {
      if (!map) return;
      const marker = mapMarkers.find(m => m.storeCode === storeCode);
      if (marker) {
        const coords = marker.getLatLng();
        map.setView(coords, 14);
        marker.openPopup();
      }
    }

    // 7. Highlight selected list card and scroll to it
    function highlightStoreCard(storeCode, shouldScroll) {
      const cards = sectionContainer.querySelectorAll('.kma-store-card');
      cards.forEach(card => {
        if (card.dataset.storeCode === storeCode) {
          card.classList.add('selected');
          if (shouldScroll) {
            card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        } else {
          card.classList.remove('selected');
        }
      });
    }

    // 8. Reset button checks
    function checkResetBtn() {
      if (!resetFiltersBtn) return;
      const hasActive = selectedProvinces.length > 0 || selectedTypes.length > 0 || searchQuery;
      resetFiltersBtn.disabled = !hasActive;
    }

    // 9. Clear all selections
    function resetAll() {
      if (searchInput) searchInput.value = '';
      searchQuery = '';
      selectedProvinces = [];
      selectedTypes = [];
      visibleCount = config.itemsPerPage;

      // Update custom select labels back to defaults
      setCustomSelectValue(csProvinceVal, labels.allProvinces);
      setCustomSelectValue(csTypeVal, labels.allTypes);

      if (csProvinceSearch) {
        csProvinceSearch.value = '';
      }
      
      // Reset province visible list items
      if (csProvinceList) {
        csProvinceList.querySelectorAll('.kma-select__option').forEach(li => li.style.display = '');
      }

      // Rebuild options Lists
      _buildProvinceOptions();
      _buildTypeOptions();

      if (clearSearchBtn) clearSearchBtn.style.display = 'none';

      checkResetBtn();
      renderStoreList();
    }

    // Dynamic Leaflet instantiation sequence
    loadLeaflet(() => {
      initMap();
      setupFilters();
      renderStoreList();
    });

    // ── Bind Interactive Event Listeners ────────────────────
    
    // Search input
    if (searchInput) {
      // Force native focus on touch devices to bypass touch event blockages by theme/Leaflet layers
      searchInput.addEventListener('touchstart', () => {
        searchInput.focus();
      });

      let debounceTimeout;
      searchInput.addEventListener('input', (e) => {
        searchQuery = e.target.value.trim();
        if (clearSearchBtn) {
          clearSearchBtn.style.display = searchQuery ? 'flex' : 'none';
        }
        
        clearTimeout(debounceTimeout);
        debounceTimeout = setTimeout(() => {
          visibleCount = config.itemsPerPage;
          renderStoreList();
          checkResetBtn();
        }, 250);
      });
    }

    // Clear search
    if (clearSearchBtn) {
      clearSearchBtn.addEventListener('click', () => {
        searchInput.value = '';
        searchQuery = '';
        clearSearchBtn.style.display = 'none';
        visibleCount = config.itemsPerPage;
        checkResetBtn();
        renderStoreList();
      });
    }

    // Custom select trigger buttons — plain click (CSS touch-action:manipulation removes 300ms delay)
    if (csProvinceEl) {
      const provinceBtn = sectionContainer.querySelector('#cs-province-btn');
      if (provinceBtn) provinceBtn.addEventListener('click', () => toggleCustomSelect('province'));
    }
    if (csTypeEl) {
      const typeBtn = sectionContainer.querySelector('#cs-type-btn');
      if (typeBtn) typeBtn.addEventListener('click', () => toggleCustomSelect('type'));
    }

    // Custom select Province search input
    if (csProvinceSearch && csProvinceList) {
      // Force native focus on touch devices to bypass touch event blockages by theme/Leaflet layers
      csProvinceSearch.addEventListener('touchstart', () => {
        csProvinceSearch.focus();
      });

      csProvinceSearch.addEventListener('input', e => {
        const q = e.target.value.toLowerCase();
        csProvinceList.querySelectorAll('.kma-select__option').forEach(li => {
          const span = li.querySelector('span');
          if (span) {
            li.style.display = span.textContent.toLowerCase().includes(q) ? '' : 'none';
          }
        });
      });
    }

    // Close dropdowns when clicking outside
    document.addEventListener('click', e => {
      // If the clicked element is detached (re-rendered synchronously), ignore it
      if (!document.body.contains(e.target)) return;
      
      const isInsideProvince = csProvinceEl && csProvinceEl.contains(e.target);
      const isInsideType = csTypeEl && csTypeEl.contains(e.target);
      if (!isInsideProvince && !isInsideType) {
        // Defer style/class changes to prevent Safari from canceling native keyboard focus
        setTimeout(() => {
          closeAllCustomSelects();
        }, 50);
      }
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') closeAllCustomSelects();
    });

    // Clicks on resets
    if (resetFiltersBtn) resetFiltersBtn.addEventListener('click', resetAll);
    if (emptyResetBtn) emptyResetBtn.addEventListener('click', resetAll);

    // Load more pagination
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', () => {
        visibleCount += config.itemsPerPage;
        renderStoreList();
      });
    }
  }

  // 10. Initialise on load
  const wrapper = document.querySelector('.kma-store-wrapper');
  if (wrapper) {
    initStoreLocator(wrapper);
  }

  // 11. Support dynamic Shopify Theme Editor previews
  document.addEventListener('shopify:section:load', (event) => {
    const sectionWrapper = event.target.querySelector('.kma-store-wrapper');
    if (sectionWrapper) {
      sectionWrapper.dataset.initialized = 'false';
      initStoreLocator(sectionWrapper);
    }
  });

})();
